import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Inbox, Loader2, LogOut, Building2, RefreshCw } from 'lucide-react';
import { API } from '@/data/catalogue';

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const LoginCard = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError('');
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
      toast.success('Welcome back');
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Login failed');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center px-5 grain relative">
      <form
        onSubmit={submit}
        data-testid="admin-login-form"
        className="w-full max-w-sm bg-[#121215] border border-[#D4AF37]/25 p-8"
      >
        <img src="/assets/img/brand_logo.png" alt="Jakson" className="h-10 w-auto mb-6" />
        <h1 className="font-serif text-3xl font-light mb-1">Enquiry Inbox</h1>
        <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-7">Authorised access only</p>
        <input
          type="email"
          required
          data-testid="admin-login-email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          className="w-full bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm mb-3 placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none"
        />
        <input
          type="password"
          required
          data-testid="admin-login-password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="Password"
          className="w-full bg-[#0A0A0B] border border-zinc-800 px-4 py-3 text-sm mb-4 placeholder:text-zinc-600 focus:border-[#D4AF37] focus:outline-none"
        />
        {error && <p data-testid="admin-login-error" className="text-red-400 text-xs mb-4 font-mono">{error}</p>}
        <button
          type="submit"
          disabled={busy}
          data-testid="admin-login-submit"
          className="w-full flex items-center justify-center gap-2 bg-[#D4AF37] text-[#0A0A0B] font-mono text-xs uppercase tracking-[0.25em] py-3.5 hover:bg-[#F5E6AD] transition-colors disabled:opacity-60"
        >
          {busy && <Loader2 size={14} className="animate-spin" />}
          {busy ? 'Signing in…' : 'Sign In'}
        </button>
        <a href="/" data-testid="admin-back-home" className="block text-center mt-5 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500 hover:text-[#D4AF37] transition-colors">
          ← Back to site
        </a>
      </form>
    </div>
  );
};

const Table = ({ cols, rows, testid }) => (
  <div className="overflow-x-auto border border-zinc-800" data-testid={testid}>
    <table className="w-full text-sm">
      <thead>
        <tr className="border-b border-[#D4AF37]/20 bg-[#0D0D0F]">
          {cols.map((c) => (
            <th key={c} className="text-left font-mono text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] px-4 py-3 whitespace-nowrap">
              {c}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.length === 0 && (
          <tr>
            <td colSpan={cols.length} className="px-4 py-10 text-center text-zinc-500 font-mono text-xs">
              Nothing here yet
            </td>
          </tr>
        )}
        {rows.map((r, i) => (
          <tr key={r.id || i} className="border-b border-zinc-800/60 hover:bg-[#121215] transition-colors" data-testid={`${testid}-row-${i}`}>
            {r.cells.map((cell, j) => (
              <td key={j} className="px-4 py-3 text-zinc-300 align-top max-w-[280px]">{cell}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

export default function AdminInbox() {
  const [user, setUser] = useState(undefined);
  const [tab, setTab] = useState('enquiries');
  const [enquiries, setEnquiries] = useState([]);
  const [dealers, setDealers] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [e, d] = await Promise.all([
        axios.get(`${API}/enquiries`, { withCredentials: true }),
        axios.get(`${API}/dealer-applications`, { withCredentials: true }),
      ]);
      setEnquiries(e.data.items || []);
      setDealers(d.data.items || []);
    } catch {
      toast.error('Could not load inbox');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    axios
      .get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setUser(res.data))
      .catch(() => setUser(null));
  }, []);

  useEffect(() => {
    if (user) load();
  }, [user]);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true }).catch(() => {});
    setUser(null);
  };

  if (user === undefined) {
    return (
      <div className="min-h-screen bg-[#0A0A0B] flex items-center justify-center">
        <Loader2 className="animate-spin text-[#D4AF37]" size={28} />
      </div>
    );
  }
  if (!user) return <LoginCard onLogin={setUser} />;

  const enquiryRows = enquiries.map((e) => ({
    id: e.id,
    cells: [
      <span className="font-mono text-xs text-zinc-500">{fmtDate(e.created_at)}</span>,
      <span className="text-[#F5F4F0]">{e.name}</span>,
      <span>{e.email}<br /><span className="text-zinc-500 text-xs">{e.phone || '—'}</span></span>,
      <span className="font-mono text-xs">{e.project_type || '—'}</span>,
      <span className="text-[#F5E6AD]">{e.collection || '—'}</span>,
      <span className="text-zinc-400">{e.message || '—'}</span>,
    ],
  }));

  const dealerRows = dealers.map((d) => ({
    id: d.id,
    cells: [
      <span className="font-mono text-xs text-zinc-500">{fmtDate(d.created_at)}</span>,
      <span className="text-[#F5F4F0]">{d.company}</span>,
      <span>{d.contact_name}</span>,
      <span>{d.email}<br /><span className="text-zinc-500 text-xs">{d.phone}</span></span>,
      <span>{d.city}</span>,
      <span className="font-mono text-xs">{d.business_type || '—'}</span>,
      <span className="text-zinc-400">{d.message || '—'}</span>,
    ],
  }));

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-[#F5F4F0]" data-testid="admin-inbox">
      <header className="border-b border-[#D4AF37]/20 glass-dark sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/assets/img/brand_logo.png" alt="Jakson" className="h-8 w-auto" />
            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">Enquiry Inbox</span>
          </div>
          <div className="flex items-center gap-4">
            <button
              data-testid="admin-refresh-btn"
              onClick={load}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400 hover:text-[#F5E6AD] transition-colors"
            >
              <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
            </button>
            <button
              data-testid="admin-logout-btn"
              onClick={logout}
              className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.2em] border border-zinc-700 px-4 py-2 hover:border-[#D4AF37] hover:text-[#F5E6AD] transition-colors"
            >
              <LogOut size={13} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10">
        <div className="flex gap-2 mb-8" data-testid="admin-tabs">
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab('enquiries')}
            className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
              tab === 'enquiries' ? 'bg-[#D4AF37] text-[#0A0A0B] border-[#D4AF37]' : 'border-zinc-800 text-zinc-400 hover:border-[#D4AF37]/60'
            }`}
          >
            <Inbox size={13} /> Enquiries ({enquiries.length})
          </button>
          <button
            data-testid="admin-tab-dealers"
            onClick={() => setTab('dealers')}
            className={`flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.2em] px-5 py-2.5 border transition-colors ${
              tab === 'dealers' ? 'bg-[#D4AF37] text-[#0A0A0B] border-[#D4AF37]' : 'border-zinc-800 text-zinc-400 hover:border-[#D4AF37]/60'
            }`}
          >
            <Building2 size={13} /> Dealer Applications ({dealers.length})
          </button>
        </div>

        {tab === 'enquiries' ? (
          <Table
            testid="admin-enquiries-table"
            cols={['Received', 'Name', 'Contact', 'Project', 'Collection', 'Message']}
            rows={enquiryRows}
          />
        ) : (
          <Table
            testid="admin-dealers-table"
            cols={['Received', 'Company', 'Contact Person', 'Email / Phone', 'City', 'Type', 'Message']}
            rows={dealerRows}
          />
        )}
      </main>
    </div>
  );
}
